import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { db } from '../utils/firebase';
import { enrichHospitalityReviewDoc } from '../utils/enrichHospitalityReviewDocs';
import { REVIEW_DETAILS_PAGE_SIZE } from '../utils/reviewDetails/reviewDetailsConstants';

/**
 * @param {object} params
 * @param {string} [params.breweryId]
 * @param {boolean} [params.enabled]
 * @param {number} [params.limit]
 */
function useHospitalityReviewsForBrewery({
  breweryId,
  enabled = false,
  limit: queryLimit = REVIEW_DETAILS_PAGE_SIZE,
}) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchReviews() {
      if (!enabled || typeof breweryId !== 'string' || breweryId.length === 0) {
        if (!cancelled) {
          setReviews([]);
          setLoading(false);
          setError(null);
        }
        return;
      }

      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        const reviewsRef = collection(db, 'hospitalityReview');
        const reviewsQuery = query(
          reviewsRef,
          where('breweryId', '==', breweryId),
          orderBy('date', 'desc'),
          limit(queryLimit),
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);

        if (cancelled) {
          return;
        }

        const reviewsData = await Promise.all(
          reviewsSnapshot.docs.map((reviewDoc) => enrichHospitalityReviewDoc(reviewDoc)),
        );

        if (!cancelled) {
          setReviews(reviewsData);
        }
      } catch (fetchError) {
        console.error('Error fetching hospitality reviews for brewery:', fetchError);
        if (!cancelled) {
          setError(fetchError.message);
          setReviews([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, [breweryId, enabled, queryLimit]);

  return { reviews, loading, error };
}

export default useHospitalityReviewsForBrewery;
